import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { resolveNoteSourceSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteSourceSyncModelUuid";
import {
  BaseSyncOperation,
  BaseSyncOperationParams,
} from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { ICreateNoteSourceOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";
import { NoteSourceModelData, NoteSourceUpsertedSyncUpdateValue } from "@/store/sources/types";

export class CreateNoteSourceOperation extends BaseSyncOperation<ICreateNoteSourceOperation> {
  get operationKind(): SyncOperationKind {
    return "CREATE_NOTE_SOURCE";
  }

  constructor({
    store,
    payload: providedPayload,
    ...params
  }: Omit<BaseSyncOperationParams<ICreateNoteSourceOperation>, "payload"> & {
    payload: Omit<ICreateNoteSourceOperation["payload"], "id"> & { id?: Uuid };
  }) {
    const noteSourceId =
      providedPayload.id ??
      resolveNoteSourceSyncModelUuid({
        noteId: providedPayload.note_id,
        sourceId: providedPayload.source_id,
      });

    const payload = {
      ...providedPayload,
      id: noteSourceId,
    };

    super({
      ...params,
      store,
      payload,
    });
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const noteSourceValue: NoteSourceUpsertedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "NOTE_SOURCE",
      model_version: 0,
      model_data: {
        id: this.payload.id,
        note_id: this.payload.note_id,
        source_id: this.payload.source_id,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };

    const noteSourceUpserted: OptimisticSyncUpdate<NoteSourceModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: noteSourceValue,
    };

    return [noteSourceUpserted];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.noteSources.recompute(this.payload.id);
  }
}
